import { Dict } from "mixpanel-browser";
import { IAnalyticsProvider } from "../../core/ports/driven/analyticsProvider";
import { reportApiError, reportAppError } from "../../../sentry";
const env = import.meta.env;

export type IAnalyticsController = ReturnType<
  typeof AnalyticsControllerFactory
>;

export const AnalyticsControllerFactory = ({
  analyticsProvider,
  circadiaEngineProvider,
  webIntercomProvider,
}: {
  analyticsProvider: IAnalyticsProvider;
  circadiaEngineProvider: ICircadiaEngineProvider;
  webIntercomProvider: IWebIntercomProvider;
}) => {
  const mixpanelApiHost = env.DEV
    ? "https://api.mixpanel.com"
    : "https://prod.circadia.link/patient-centric-app-v2/mixpanel";

  analyticsProvider.init(env.VITE_ANALYTICS_KEY, {
    api_host: mixpanelApiHost,
    persistence: "localStorage",
  });

  const resetEvent = () => {
    analyticsProvider.reset();
  };
  const logEvent = (
    eventName: string,
    eventProperties?: Dict,
    identity?: string
  ) => {
    if (identity) {
      analyticsProvider.identify(identity);
    }
    const eventProp: Dict = {
      ...eventProperties,
      appVersion: APP_VERSION,
    };

    if (circadiaEngineProvider.isWindowCircadia()) {
      eventProp.intfv = circadiaEngineProvider.intfv;
      eventProp.plat = circadiaEngineProvider.plat;
    }

    analyticsProvider.track(eventName, eventProp);
    // const intercomEventProp = { ...eventProp }
    // delete intercomEventProp.token
    // delete intercomEventProp.selectedFacilityId
    // delete intercomEventProp.cUserId
    // webIntercomProvider.trackEvent(eventName, intercomEventProp);
  };

  const timeEvent = (eventName: string) => {
    analyticsProvider.timeEvent(eventName);
  };

  const setPeople = (obj: {
    name: string;
    role: string;
    email: string;
    userID: string;
  }) => {
    analyticsProvider.setPeople(obj);
  };

  const identify = (identity: string) => {
    analyticsProvider.identify(identity);
  };

  // Enhanced error logging that reports to both Mixpanel and Sentry
  const logApiError = (
    error: Error,
    context: {
      method: string;
      url?: string;
      status?: number;
      apiName?: string;
      requestBody?: unknown;
      responseBody?: unknown;
      duration?: number;
    }
  ) => {
    // Log to Mixpanel (existing behavior)
    logEvent("api-failure", {
      apiName: context.apiName || "unknown",
      method: context.method,
      status: context.status,
      url: context.url,
      error_message: error.message,
      error_name: error.name,
    });

    if (context.status && context.status !== 404 && context.status !== 401) {
      reportApiError(error, {
        method: context.method,
        url: context.url,
        status: context.status,
        requestBody: context.requestBody,
        responseBody: context.responseBody,
        duration: context.duration,
      });
    }
  };

  const logAppError = (
    error: Error,
    context: {
      component?: string;
      action?: string;
      userId?: string;
      additionalData?: Record<string, unknown>;
    } = {}
  ) => {
    // Log to Mixpanel
    logEvent("app-error", {
      component: context.component || "unknown",
      action: context.action || "unknown",
      error_message: error.message,
      error_name: error.name,
      user_id: context.userId,
      ...context.additionalData,
    });

    // Also report to Sentry
    reportAppError(error, context);
  };

  return {
    resetEvent,
    logEvent,
    timeEvent,
    setPeople,
    identify,
    logApiError,
    logAppError,
  };
};
